import React, { useState, useEffect } from 'react';
import {
    collection,
    addDoc,
    setDoc,
    doc,
    serverTimestamp,
    getDocs,
    query,
    where
} from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { db, secondaryAuth } from '../services/firebase';
import { locationsByState, talukasByDistrict } from '../constants/locations';
import LoadingSpinner from '../components/LoadingSpinner';
import { useNavigate } from 'react-router-dom';

const SuperAdminFranchiseManagement = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [franchises, setFranchises] = useState([]);
    const [filteredFranchises, setFilteredFranchises] = useState([]);
    const [searchTerm, setSearchTerm] = useState(''); // Empty by default
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        ownerName: '',
        email: '',
        phone: '',
        password: '',
        city: '',
        state: '',
        district: '',
        franchiseTaluka: '',
        // For Access Control
        accessState: '',
        accessDistrict: '',
        accessTaluka: '',
        assignedAreas: [], // Array of {state, district, taluka}

        validityStart: '',
        validityEnd: ''
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedFranchise, setSelectedFranchise] = useState(null);

    // Fetch franchises on mount
    useEffect(() => {
        fetchFranchises();
    }, []);

    // Real-time search functionality - filters as soon as you type any letter
    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredFranchises(franchises);
        } else {
            const searchLower = searchTerm.toLowerCase().trim();
            const filtered = franchises.filter(franchise => {
                // Search in all text fields (excluding ownerName)
                const searchableText = `
                    ${franchise.name || ''} 
                    ${franchise.email || ''} 
                    ${franchise.phone || ''} 
                    ${franchise.district || ''} 
                    ${franchise.state || ''} 
                    ${franchise.city || ''} 
                    ${franchise.status || ''}
                    ${franchise.taluka || ''}
                    ${franchise.assignedAreas?.map(area => `${area.taluka} ${area.district}`).join(' ') || ''}
                `.toLowerCase();
                
                return searchableText.includes(searchLower);
            });
            setFilteredFranchises(filtered);
        }
    }, [searchTerm, franchises]);

    const fetchFranchises = async () => {
        try {
            const q = query(collection(db, 'franchises'));
            const querySnapshot = await getDocs(q);
            const franchiseList = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setFranchises(franchiseList);
            setFilteredFranchises(franchiseList);
        } catch (err) {
            console.error("Error fetching franchises:", err);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleStateChange = (e) => {
        const newState = e.target.value;
        const { name } = e.target;

        setFormData(prev => ({
            ...prev,
            [name]: newState,
            [name === 'state' ? 'district' : 'accessDistrict']: '',
            [name === 'state' ? 'franchiseTaluka' : 'accessTaluka']: '',
        }));
    };

    const handleDistrictChange = (e) => {
        const newDistrict = e.target.value;
        const { name } = e.target;

        setFormData(prev => ({
            ...prev,
            [name]: newDistrict,
            [name === 'district' ? 'franchiseTaluka' : 'accessTaluka']: '',
        }));
    };

    const handleAddAccessArea = () => {
        const { state, accessDistrict, accessTaluka, assignedAreas } = formData;

        // Use main state for access control
        const accessState = state;

        if (!accessState || !accessDistrict || !accessTaluka) return;

        // Check for duplicates
        const exists = assignedAreas.some(area =>
            area.state === accessState &&
            area.district === accessDistrict &&
            area.taluka === accessTaluka
        );

        if (!exists) {
            setFormData(prev => ({
                ...prev,
                assignedAreas: [...prev.assignedAreas, {
                    state: accessState,
                    district: accessDistrict,
                    taluka: accessTaluka
                }],
                // Reset selection after adding
                accessTaluka: ''
            }));
        }
    };

    const handleRemoveAccessArea = (index) => {
        setFormData(prev => ({
            ...prev,
            assignedAreas: prev.assignedAreas.filter((_, i) => i !== index)
        }));
    };

    const validateForm = () => {
        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            return "Invalid email address format";
        }

        // Phone validation
        const phoneRegex = /^\d{10}$/;
        if (!phoneRegex.test(formData.phone)) {
            return "Phone number must be exactly 10 digits";
        }

        // Password validation
        if (formData.password.length < 6) {
            return "Password must be at least 6 characters long";
        }

        // Assignments validation
        if (formData.assignedAreas.length === 0) {
            return "Please assign at least one territory";
        }

        // Date validation
        if (formData.validityStart && formData.validityEnd) {
            const start = new Date(formData.validityStart);
            const end = new Date(formData.validityEnd);
            if (end <= start) {
                return "End date must be after start date";
            }
        }

        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        // Basic validation for new franchise (password required)
        if (!isEditing && formData.password.length < 6) {
            setError("Password must be at least 6 characters long");
            setLoading(false);
            return;
        }

        const validationError = validateForm();
        if (validationError && !(isEditing && validationError.includes('Password'))) {
            setError(validationError);
            setLoading(false);
            return;
        }

        try {
            let uid = editingId;

            if (!isEditing) {
                // 1. Create User in Firebase Auth (using secondary app to avoid logging out admin)
                const userCredential = await createUserWithEmailAndPassword(
                    secondaryAuth,
                    formData.email,
                    formData.password
                );
                uid = userCredential.user.uid;

                // 2. Save role in users/{uid}
                await setDoc(doc(db, 'users', uid), {
                    email: formData.email,
                    phone: formData.phone,
                    userType: 'franchise',
                    createdAt: serverTimestamp(),
                });
            }

            // 3. Add/Update Franchise DB structure under franchises/{franchiseId}
            const franchiseData = {
                name: formData.name,
                ownerName: formData.ownerName,
                email: formData.email,
                phone: formData.phone,
                city: formData.city,
                state: formData.state,
                district: formData.district,
                taluka: formData.franchiseTaluka, // Physical location
                assignedAreas: formData.assignedAreas,
                validityStart: formData.validityStart,
                validityEnd: formData.validityEnd,
                status: formData.status || 'active',
                updatedAt: serverTimestamp()
            };

            if (!isEditing) {
                franchiseData.createdAt = serverTimestamp();
                franchiseData.franchiseId = uid;
                franchiseData.isLimitedQuota = false;
            }

            await setDoc(doc(db, 'franchises', uid), franchiseData, { merge: true });

            setSuccess(`Franchise ${isEditing ? 'updated' : 'created'} successfully!`);
            resetForm();
            setShowModal(false);
            fetchFranchises();

            if (!isEditing) await secondaryAuth.signOut();

        } catch (err) {
            console.error("Error saving franchise:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            ownerName: '',
            email: '',
            phone: '',
            password: '',
            city: '',
            state: '',
            district: '',
            franchiseTaluka: '',
            accessState: '',
            accessDistrict: '',
            accessTaluka: '',
            assignedAreas: [],
            validityStart: '',
            validityEnd: '',
            status: 'active'
        });
        setIsEditing(false);
        setEditingId(null);
    };

    const handleEdit = (franchise) => {
        setFormData({
            name: franchise.name || '',
            ownerName: franchise.ownerName || '',
            email: franchise.email || '',
            phone: franchise.phone || '',
            password: '', // Don't show password
            city: franchise.city || '',
            state: franchise.state || '',
            district: franchise.district || '',
            franchiseTaluka: franchise.taluka || '',
            accessState: '',
            accessDistrict: '',
            accessTaluka: '',
            assignedAreas: franchise.assignedAreas || [],
            validityStart: franchise.validityStart || '',
            validityEnd: franchise.validityEnd || '',
            status: franchise.status || 'active'
        });
        setEditingId(franchise.id);
        setIsEditing(true);
        setShowModal(true);
    };

    const handleView = (franchise) => {
        setSelectedFranchise(franchise);
        setShowViewModal(true);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        // Real-time filtering happens automatically via useEffect
    };

    // Helper function to highlight matching text
    const highlightText = (text, searchTerm) => {
        if (!text || !searchTerm || searchTerm.trim() === '') return text;
        
        try {
            const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
            const parts = text.split(regex);
            
            return parts.map((part, index) => 
                regex.test(part) ? 
                    <span key={index} className="bg-yellow-200 text-gray-900 font-semibold">{part}</span> : 
                    part
            );
        } catch (e) {
            return text;
        }
    };

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Franchise Management</h1>
                <button
                    onClick={() => {
                        resetForm();
                        setShowModal(true);
                    }}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                    Add New Franchise
                </button>
            </div>

            {loading && <LoadingSpinner />}

            {/* Search Bar Section - Empty by default */}
            <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-100 p-4">
                <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                    <div className="relative flex-1 w-full">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                        <input
                            type="text"
                            placeholder="Search by Name, Email, Phone, District, State, City, Status, Territory..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        {searchTerm && (
                            <button
                                onClick={handleClearSearch}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                            >
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                        </svg>
                        <span>Found {filteredFranchises.length} of {franchises.length} franchises</span>
                    </div>
                </div>
                {searchTerm && (
                    <div className="mt-2 text-xs text-gray-500">
                        Searching for: <span className="font-semibold text-blue-600">"{searchTerm}"</span>
                    </div>
                )}
            </div>

            {/* Franchise List */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 border-b border-gray-100">
                            <tr>
                                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Franchise Name</th>
                                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Owner</th>
                                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase">District & State</th>
                                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Assigned Talukas</th>
                                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Validity</th>
                                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filteredFranchises.length > 0 ? (
                                filteredFranchises.map((franchise) => (
                                    <tr key={franchise.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4">
                                            <div className="font-medium text-gray-900">
                                                {searchTerm ? highlightText(franchise.name, searchTerm) : franchise.name}
                                            </div>
                                            <div className="text-sm text-gray-500">
                                                {searchTerm ? highlightText(franchise.email, searchTerm) : franchise.email}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm text-gray-900">
                                                {franchise.ownerName}
                                            </div>
                                            <div className="text-sm text-gray-500">
                                                {searchTerm ? highlightText(franchise.phone, searchTerm) : franchise.phone}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500">
                                            {searchTerm ? 
                                                highlightText(`${franchise.district ? `${franchise.district}, ` : ''}${franchise.state}`, searchTerm) : 
                                                `${franchise.district ? `${franchise.district}, ` : ''}${franchise.state}`
                                            }
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500">
                                            {franchise.assignedAreas && franchise.assignedAreas.length > 0
                                                ? franchise.assignedAreas.map(area => `${area.taluka} (${area.district})`).join(', ')
                                                : franchise.assignedTalukas ? franchise.assignedTalukas.join(', ') : 'No Areas Assigned'}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${franchise.status === 'active'
                                                ? 'bg-green-100 text-green-700'
                                                : 'bg-red-100 text-red-700'
                                                }`}>
                                                {franchise.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500">
                                            {franchise.validityEnd}
                                        </td>
                                        <td className="px-6 py-4 text-sm font-medium">
                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={() => handleView(franchise)}
                                                    className="text-blue-600 hover:text-blue-900 bg-blue-50 px-3 py-1 rounded"
                                                >
                                                    View
                                                </button>
                                                <button
                                                    onClick={() => handleEdit(franchise)}
                                                    className="text-amber-600 hover:text-amber-900 bg-amber-50 px-3 py-1 rounded"
                                                >
                                                    Edit
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                                        {searchTerm ? (
                                            <div>
                                                <svg className="mx-auto h-12 w-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <p className="text-gray-600">No franchises found matching "<span className="font-semibold">{searchTerm}</span>"</p>
                                                <button 
                                                    onClick={handleClearSearch}
                                                    className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                                                >
                                                    Clear search
                                                </button>
                                            </div>
                                        ) : (
                                            'No franchises found. Add one to get started.'
                                        )}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Add Franchise Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                            <h2 className="text-xl font-bold text-gray-800">
                                {isEditing ? 'Edit Franchise' : 'Add New Franchise'}
                            </h2>
                            <button
                                onClick={() => setShowModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            {error && (
                                <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
                                    {error}
                                </div>
                            )}
                            {success && (
                                <div className="bg-green-50 text-green-600 p-3 rounded-lg text-sm">
                                    {success}
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Franchise Name <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        name="name"
                                        required
                                        value={formData.name}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Owner Name <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        name="ownerName"
                                        required
                                        value={formData.ownerName}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                                    <input
                                        type="email"
                                        name="email"
                                        required
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone <span className="text-red-500">*</span></label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        required
                                        value={formData.phone}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="9876543210"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Password {!isEditing && <span className="text-red-500">*</span>}</label>
                                    <input
                                        type="text"
                                        name="password"
                                        required={!isEditing}
                                        minLength="6"
                                        value={formData.password}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                                        placeholder={isEditing ? "Leave blank to keep current" : "Min. 6 characters"}
                                        disabled={isEditing}
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        {isEditing ? "Password cannot be changed from here" : "This password will be used by franchise for login"}
                                    </p>
                                </div>
                                {isEditing && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                        <select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        >
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                        </select>
                                    </div>
                                )}
                            </div>

                            {/* Section 1: Franchise Address */}
                            <div className="border-t border-gray-100 pt-4">
                                <h3 className="text-lg font-semibold text-gray-800 mb-3">Franchise Address</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">State <span className="text-red-500">*</span></label>
                                        <select
                                            name="state"
                                            required
                                            value={formData.state}
                                            onChange={handleStateChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        >
                                            <option value="">Select State</option>
                                            {Object.keys(locationsByState).map(state => (
                                                <option key={state} value={state}>{state}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">District <span className="text-red-500">*</span></label>
                                        <select
                                            name="district"
                                            required
                                            value={formData.district}
                                            onChange={handleDistrictChange}
                                            disabled={!formData.state}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                                        >
                                            <option value="">Select District</option>
                                            {formData.state && locationsByState[formData.state]?.map(district => (
                                                <option key={district} value={district}>{district}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Taluka <span className="text-red-500">*</span></label>
                                        <select
                                            name="franchiseTaluka"
                                            required
                                            value={formData.franchiseTaluka}
                                            onChange={handleChange}
                                            disabled={!formData.district}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                                        >
                                            <option value="">Select Taluka</option>
                                            {formData.district && talukasByDistrict[formData.district]?.map(taluka => (
                                                <option key={taluka} value={taluka}>{taluka}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">City / Village <span className="text-red-500">*</span></label>
                                        <input
                                            type="text"
                                            name="city"
                                            required
                                            value={formData.city}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Section 2: Operational Access */}
                            <div className="border-t border-gray-100 pt-4">
                                <h3 className="text-lg font-semibold text-gray-800 mb-3">Operational Access Control</h3>
                                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                                    <h4 className="text-sm font-medium text-gray-700 mb-3">Add Territory Access</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-500 mb-1">State: {formData.state || 'Selected above'}</label>
                                            <select
                                                name="accessDistrict"
                                                value={formData.accessDistrict}
                                                onChange={handleDistrictChange}
                                                disabled={!formData.state}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm disabled:bg-gray-100"
                                            >
                                                <option value="">Select District</option>
                                                {formData.state && locationsByState[formData.state]?.map(district => (
                                                    <option key={district} value={district}>{district}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-500 mb-1">Taluka</label>
                                            <select
                                                name="accessTaluka"
                                                value={formData.accessTaluka}
                                                onChange={handleChange}
                                                disabled={!formData.accessDistrict}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm disabled:bg-gray-100"
                                            >
                                                <option value="">Select Taluka</option>
                                                {formData.accessDistrict && talukasByDistrict[formData.accessDistrict]?.map(taluka => (
                                                    <option key={taluka} value={taluka}>{taluka}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleAddAccessArea}
                                        disabled={!formData.accessTaluka}
                                        className="w-full md:w-auto px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                                    >
                                        Add Territory
                                    </button>
                                </div>

                                {/* Assigned Territories List */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Assigned Territories <span className="text-red-500">*</span></label>
                                    {formData.assignedAreas.length > 0 ? (
                                        <div className="flex flex-wrap gap-2">
                                            {formData.assignedAreas.map((area, index) => (
                                                <span
                                                    key={index}
                                                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                                                >
                                                    {area.taluka} ({area.district})
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveAccessArea(index)}
                                                        className="ml-2 text-blue-600 hover:text-blue-800 focus:outline-none"
                                                    >
                                                        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                        </svg>
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-sm text-gray-500 italic p-2 border border-dashed border-gray-300 rounded-lg text-center">
                                            No territories assigned yet. Add territories above.
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-gray-100 pt-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Validity Start <span className="text-red-500">*</span></label>
                                    <input
                                        type="date"
                                        name="validityStart"
                                        required
                                        value={formData.validityStart}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Validity End <span className="text-red-500">*</span></label>
                                    <input
                                        type="date"
                                        name="validityEnd"
                                        required
                                        value={formData.validityEnd}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            </div>

                            <div className="pt-4 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    {isEditing ? 'Update Franchise' : 'Create Franchise'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* View Franchise Modal */}
            {showViewModal && selectedFranchise && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                            <h2 className="text-xl font-bold text-gray-800">Franchise Details</h2>
                            <button onClick={() => setShowViewModal(false)} className="text-gray-400 hover:text-gray-600">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="p-6 space-y-6">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-sm text-gray-500">Franchise Name</p>
                                    <p className="font-semibold">{selectedFranchise.name}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Owner Name</p>
                                    <p className="font-semibold">{selectedFranchise.ownerName}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Email</p>
                                    <p className="font-semibold">{selectedFranchise.email}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Phone</p>
                                    <p className="font-semibold">{selectedFranchise.phone}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Location</p>
                                    <p className="font-semibold">{selectedFranchise.city}, {selectedFranchise.taluka}, {selectedFranchise.district}, {selectedFranchise.state}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Status</p>
                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${selectedFranchise.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                        {selectedFranchise.status}
                                    </span>
                                </div>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500 mb-2">Assigned Territories</p>
                                <div className="flex flex-wrap gap-2">
                                    {selectedFranchise.assignedAreas?.map((area, idx) => (
                                        <span key={idx} className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm">
                                            {area.taluka} ({area.district})
                                        </span>
                                    ))}
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-sm text-gray-500">Validity Start</p>
                                    <p className="font-semibold">{selectedFranchise.validityStart}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Validity End</p>
                                    <p className="font-semibold">{selectedFranchise.validityEnd}</p>
                                </div>
                            </div>
                        </div>
                        <div className="p-6 border-t border-gray-100 flex justify-end">
                            <button onClick={() => setShowViewModal(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SuperAdminFranchiseManagement;